import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react'
import { CommomnMetadataListItem, masterActivityLogData, ProcessFlow, StatusListItem, TRMConfigurationItem } from '../../types/listDataTypes'
import { UserContext } from './UserContextProvider'
import { anewRListsHttp } from '../../shared/services/api/anew-r-lists-http'
import { ListNames } from '../../shared/constants/globalConstants'
import { getAllDeferredMetadata, getAllInitialMetadata } from '../../modules/TRM/services/trmMetadataService'
import { buildVersionChecker } from '../../global'

type MasterActivityLogState = {
    items: masterActivityLogData[];
    apiSkip: number;
    hasMore: boolean;
    allDataLoaded: boolean;
};

const defaultMetadata = {
    commonMetadata: [] as CommomnMetadataListItem[],
    statusMetadata: [] as StatusListItem[],
    notificationMetadata: [] as any[],
    activityLogMetadata: [] as any[],
    processFlowMetadata: [] as ProcessFlow[],
    isMetadataLoading: true,
    isDeferredMetadataLoading: true,
    setIsMetadatUpdated: () => {},
    isAIChatEnabled: false,
    dlEmail: '',
    cancelEmail: '',
    state: {} as MasterActivityLogState,
    setState: (() => {}) as React.Dispatch<React.SetStateAction<MasterActivityLogState>>
}

const MetadataContext = createContext(defaultMetadata)
const MetadataContextProvider  = ({ children }: { children: React.ReactNode }) => {
    const {me} = useContext(UserContext)
    const [metadata,setMetadata]=useState(defaultMetadata)
    const [isMetadatUpdated, setIsMetadatUpdated] = useState<boolean>(false)
    const [state, setState] = useState<MasterActivityLogState>({ items: [], apiSkip: 0, hasMore: true, allDataLoaded: false });
    const metadataRequestIdRef = useRef(0)
    useEffect(()=>{
      if( me.userId !== undefined && me.userId !== '' && me.userId !== null){
          getMetadata()  
      }  
    },[me.userId,isMetadatUpdated])

    const getMetadata = async()=>{
    const requestId = ++metadataRequestIdRef.current
    await buildVersionChecker() // Check for build version updates and refresh if needed
    // 2. FETCH APPLICATION CONFIGURATION
    // The configuration flags and the critical metadata don't depend on each other, so load them
    // together (both after the build check, which may clear IndexedDB). loadConfigFlags never rejects.
    const loadConfigFlags = async () => {
    let chatEnabled = true;
    let dlemail = ''
    let cancelEmail=''
    try {
        // Fetch the configuration list from API
        const configListResponse = await (await anewRListsHttp().getList(ListNames.TRMConfigurationList)).query().get();
        const configList = Array.from(configListResponse) as TRMConfigurationItem[];
        
        const aiConfigItem = configList?.find((item) => (item?.FeatureName ?? '').toLowerCase() === 'aichat');
        const dlEmail=configList?.find((item) => item?.FeatureName === 'WorkOrderEmails');
        const cancelEmailConfig=configList?.find((item) => item?.FeatureName === 'CancellationEmails')
        if (aiConfigItem?.IsEnabled === false) {
            chatEnabled = false;
        }
        dlemail=dlEmail?.Value as string
        cancelEmail=cancelEmailConfig?.Value as string
    } catch (error) {
        console.warn("Could not load TRMConfigurationList, AI Chat defaulted to enabled.", error);
    }
    return { chatEnabled, dlemail, cancelEmail }
    }

    const [{ chatEnabled, dlemail, cancelEmail }, criticalMetadata] = await Promise.all([
      loadConfigFlags(),
      getAllInitialMetadata(),
    ]);

      if (requestId !== metadataRequestIdRef.current) {
        return
      }

      setMetadata((prev) => ({
            ...prev,
            commonMetadata: criticalMetadata[ListNames.TRMCommonMetadataList] || [],
            statusMetadata: criticalMetadata[ListNames.TRMStatusMetadataList]?.sort((a, b) => a?.StatusCode - b?.StatusCode) || [],
            isMetadataLoading: false,
            isDeferredMetadataLoading: true,
            setIsMetadatUpdated: () => setIsMetadatUpdated(!isMetadatUpdated),
            isAIChatEnabled: chatEnabled,
            dlEmail:dlemail,
            cancelEmail:cancelEmail
          }));

      // Non-critical metadata is hydrated after the app is usable. Write paths that
      // depend on it (notification dispatch, activity-log text) must not read these
      // arrays straight from context during this window - they go through
      // ensureDeferredList() in trmMetadataService, which awaits this same load.
      void (async () => {
        try {
          const deferredMetadata = await getAllDeferredMetadata();
          if (requestId !== metadataRequestIdRef.current) {
            return
          }
          setMetadata((prev) => {
            return {
              ...prev,
              notificationMetadata: deferredMetadata[ListNames.TRMNotificationsMetadataList] || prev.notificationMetadata,
              activityLogMetadata: deferredMetadata[ListNames.TRMActvityLogMetadataList] || prev.activityLogMetadata,
              processFlowMetadata: deferredMetadata[ListNames.TRMProcessFlowMetadataList] || prev.processFlowMetadata,
              isDeferredMetadataLoading: false,
            }
          })
        } catch (error) {
          console.warn('Deferred metadata load failed.', error)
          if (requestId === metadataRequestIdRef.current) {
            setMetadata((prev) => ({ ...prev, isDeferredMetadataLoading: false }))
          }
        }
      })()
    }

  const contextValue = useMemo(() => ({ ...metadata, state, setState }), [metadata, state])

  return (
    <MetadataContext.Provider value={contextValue}>
        <div>{children}</div>
    </MetadataContext.Provider>
  )
}

export {MetadataContextProvider,defaultMetadata,MetadataContext}
